import React from 'react';
import Immutable from 'immutable';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import isEmpty from 'lodash/isEmpty';
import { TransactionalSolidButton } from '@bibliocommons/base-buttons';

import { Pill } from 'app/components/shared/Pill';
import TokenizedInput from 'app/components/shared/TokenizedInput';
import { selectTagsByBibs, selectTagsTransactions } from 'app/selectors/ugc/TagsSelector';
import { TAG_TYPES } from 'app/constants/TagsConstants';

import './TagsEditor.scss';

export default function TagsEditor({ metadataId, handleClose, tagsActions }) {
  const tagsTransactions = useSelector(selectTagsTransactions);
  const existingTags = useSelector(selectTagsByBibs).get(metadataId, Immutable.Map());
  const unsavedTokens = {};

  function handleAddTag(type, value) {
    delete unsavedTokens[type];
    tagsActions.addTags(metadataId, [{ metadataId, type, value }]);
  }

  function handleRemoveTag(tagId, tagType) {
    return tagsActions.removeTag(metadataId, tagId, tagType);
  }

  function handleInputChange(type, value) {
    unsavedTokens[type] = value;
  }

  function handleCloseEditor() {
    const tagsData = [];
    // submit all unsaved tokens
    if (!isEmpty(unsavedTokens)) {
      Object.entries(unsavedTokens).forEach(([type, value]) => {
        tagsData.push({ metadataId, type, value });
      });
      tagsActions.addTags(metadataId, tagsData);
    }
    handleClose();
  }

  function renderTags(tag) {
    const tagId = tag?.get('id');
    const tagType = tag?.get('type');

    return (
      <Pill key={`tag-${tagType}-${tagId}`} handleDismiss={() => handleRemoveTag(tagId, tagType)}>
        {tag.get('value')}
      </Pill>
    );
  }

  function renderTokenizedInput(type) {
    const tags = existingTags?.filter(tag => tag.get('type') === type).toArray() ?? [];

    return (
      <TokenizedInput
        id={`${type.toLowerCase()}-tags-field`}
        maxInputLength={50}
        tokens={tags}
        getTokenKey={tag => tag?.get('id')}
        renderToken={value => renderTags(value)}
        renderNewToken={value => renderTags(Immutable.Map({ metadataId, type, value }))}
        isSaving={tagsTransactions.getIn([metadataId, 'isAdding'], false)}
        handleTokenSave={value => handleAddTag(type, value)}
        handleInputChange={value => handleInputChange(type, value)}
        ariaDescribedBy={`tag_help_${type.toLowerCase()}`}
      />
    );
  }

  function renderTagsEditorForm() {
    return (
      <div className="tags-form">
        <div className="form-group">
          <label htmlFor="genre-tags-field">
            <FormattedMessage id="tag_heading_genre" />
          </label>
          {renderTokenizedInput(TAG_TYPES.GENRE)}
          <p className="form-help" id="tag_help_genre">
            <FormattedMessage id="tag_help_genre" />
          </p>
        </div>

        <div className="form-group">
          <label htmlFor="mood-tags-field">
            <FormattedMessage id="tag_heading_mood" />
          </label>
          {renderTokenizedInput(TAG_TYPES.MOOD)}
          <p className="form-help" id="tag_help_mood">
            <FormattedMessage id="tag_help_mood" />
          </p>
        </div>

        <div className="form-group">
          <label htmlFor="subject-tags-field">
            <FormattedMessage id="tag_heading_subject" />
          </label>
          {renderTokenizedInput(TAG_TYPES.SUBJECT)}
          <p className="form-help" id="tag_help_subject">
            <FormattedMessage id="tag_help_subject" />
          </p>
        </div>

        <div className="tag-editor-footer">
          <TransactionalSolidButton dataKey="submit-button" handleClick={handleCloseEditor}>
            <FormattedMessage id="done" />
          </TransactionalSolidButton>
        </div>
      </div>
    );
  }

  return (
    <div className="cp-tags-editor">
      <div className="tags-editor-heading">
        <FormattedMessage id="add_and_remove_tags" />
      </div>
      <p className="tags-editor-help">
        <FormattedMessage id="tags_form_help" />
      </p>
      {renderTagsEditorForm()}
    </div>
  );
}

TagsEditor.propTypes = {
  metadataId: PropTypes.string.isRequired,
  handleClose: PropTypes.func.isRequired,
  tagsActions: PropTypes.objectOf(PropTypes.func).isRequired
};
